import React from 'react';
import { Youtube, Facebook, Instagram, Linkedin, Check, AlertCircle, Loader2, X, RefreshCw } from 'lucide-react';
import { motion } from 'framer-motion';
import { PLATFORM_COLORS, PLATFORMS } from '../../../utils/socialMediaHelpers';

const PLATFORM_ICONS = {
    youtube: Youtube,
    facebook: Facebook,
    instagram: Instagram,
    linkedin: Linkedin,
};

const PlatformCard = ({
    platform,
    integration,
    isConnecting,
    onConnect,
    onDisconnect,
    onRefresh,
}) => {
    const Icon = PLATFORM_ICONS[platform] || Youtube;
    const platformInfo = PLATFORMS[platform];
    const colors = PLATFORM_COLORS[platform];
    const isConnected = !!integration;
    const isExpired = integration?.status === 'expired';
    const needsReconnect = integration?.status === 'needs_reconnect';

    const getStatusBadge = () => {
        if (!isConnected) return null;

        if (isExpired || needsReconnect) {
            return (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
                    <AlertCircle className="w-3 h-3" />
                    Reconnect
                </span>
            );
        }

        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                <Check className="w-3 h-3" />
                Connected
            </span>
        );
    };

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`relative p-6 rounded-2xl border transition-all ${
                isConnected
                    ? `${colors.border} ${colors.dark.border} bg-white dark:bg-slate-800/50`
                    : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/30 hover:border-slate-300 dark:hover:border-slate-600'
            }`}
        >
            {/* Platform Icon and Info */}
            <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                    <div className={`p-3 rounded-xl ${colors.bg} ${colors.dark.bg}`}>
                        <Icon className={`w-6 h-6 ${colors.text} ${colors.dark.text}`} />
                    </div>
                    <div>
                        <h3 className="font-semibold text-slate-900 dark:text-white">
                            {platformInfo?.name || platform}
                        </h3>
                        {isConnected && integration?.account_name && (
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                                @{integration.account_name}
                            </p>
                        )}
                    </div>
                </div>
                {getStatusBadge()}
            </div>

            {/* Connected Account Avatar */}
            {isConnected && integration?.avatar_url && (
                <div className="flex items-center gap-3 mb-4 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                    <img
                        src={integration.avatar_url}
                        alt={integration.account_name}
                        className="w-10 h-10 rounded-full border-2 border-white dark:border-slate-700 shadow"
                    />
                    <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 dark:text-white truncate">
                            {integration.display_name || integration.account_name}
                        </p>
                        {integration.followers_count !== undefined && (
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                                {integration.followers_count.toLocaleString()} followers
                            </p>
                        )}
                    </div>
                </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-2">
                {!isConnected ? (
                    <button
                        onClick={() => onConnect(platform)}
                        disabled={isConnecting}
                        className={`flex-1 py-2.5 px-4 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
                            isConnecting
                                ? 'bg-slate-100 dark:bg-slate-700 text-slate-400 cursor-not-allowed'
                                : `${colors.bg} ${colors.dark.bg} ${colors.text} ${colors.dark.text} hover:opacity-80`
                        }`}
                    >
                        {isConnecting ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Connecting...
                            </>
                        ) : (
                            `Connect ${platformInfo?.name || platform}`
                        )}
                    </button>
                ) : (
                    <>
                        {(isExpired || needsReconnect) && (
                            <button
                                onClick={() => onRefresh(integration.id)}
                                className={`flex-1 py-2.5 px-4 rounded-xl font-medium transition-all flex items-center justify-center gap-2 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 hover:bg-yellow-200 dark:hover:bg-yellow-900/50`}
                            >
                                <RefreshCw className="w-4 h-4" />
                                Reconnect
                            </button>
                        )}
                        <button
                            onClick={() => onDisconnect(integration.id)}
                            className="py-2.5 px-4 rounded-xl font-medium transition-all flex items-center justify-center gap-2 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-red-100 dark:hover:bg-red-900/30 hover:text-red-600 dark:hover:text-red-400"
                        >
                            <X className="w-4 h-4" />
                            Disconnect
                        </button>
                    </>
                )}
            </div>
        </motion.div>
    );
};

export default PlatformCard;
